import React from "react";
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Box,
  Chip,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbDownOutlinedIcon from "@mui/icons-material/ThumbDownOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PeopleIcon from "@mui/icons-material/People";
import { BASE_API_URL } from "../../config";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

type GalleryCardProps = {
  item: any;
  userVote: string | null;
  onVote: (captureId: number, voteType: string) => void;
  isAuthenticated: boolean;
  onClick: (captureId: number) => void;
};

const getTierListUrl = (item: any): string | null => {
  if (!item.list_type) return null;
  const lt = item.list_type.toLowerCase();
  const gb = item.group_by && item.group_by !== "All" ? item.group_by : null;
  const basePath = gb
    ? `/tier-list/${lt}/${encodeURIComponent(gb)}`
    : `/tier-list/${lt}`;
  const params = new URLSearchParams();
  if (item.custom_tiers) {
    try {
      const tiers = typeof item.custom_tiers === "string" ? JSON.parse(item.custom_tiers) : item.custom_tiers;
      if (Array.isArray(tiers) && tiers.length > 0) {
        params.set("customTiers", btoa(JSON.stringify(tiers)));
      }
    } catch { /* ignore parse errors */ }
  }
  params.set("from", String(item.Id));
  const qs = params.toString();
  return qs ? `${basePath}?${qs}` : basePath;
};

const trackTryClick = (captureId: number) => {
  fetch(`${BASE_API_URL}/api/gallery/try`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ captureId }),
  }).catch(() => {});
};

const getTypeLabel = (item: any): string | null => {
  if (!item.list_type) return null;
  if (item.group_by && item.group_by !== "All") {
    return `${item.list_type} · ${item.group_by}`;
  }
  return item.list_type;
};

const GalleryCard = ({ item, userVote, onVote, isAuthenticated, onClick }: GalleryCardProps) => {
  const { t } = useTranslation();
  const displayName = item.alias_name || item.user_name?.split(" ")[0] || t("gallery.anonymous");
  const inspiredByName = item.inspired_by_alias || item.inspired_by_name?.split(" ")[0];
  const typeLabel = getTypeLabel(item);
  const tryUrl = getTierListUrl(item);

  return (
    <Card
      id="gallery-card"
      sx={{
        cursor: "pointer",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
        },
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      onClick={() => onClick(item.Id)}
    >
      {item.thumbnail ? (
        <CardMedia
          component="img"
          image={item.thumbnail}
          alt={item.title || "Tier List"}
          sx={{ height: 200, objectFit: "cover" }}
        />
      ) : (
        <Box
          sx={{
            height: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "var(--hover-overlay)",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("gallery.noPreview")}
          </Typography>
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1, pb: 0 }}>
        <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
          {item.title || item.type || "Untitled"}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {t("gallery.by")}{" "}
            <RouterLink
              to={`/profile/${item.alias_name || item.user_id}`}
              onClick={(e) => e.stopPropagation()}
              style={{ color: "inherit", textDecoration: "none" }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
            >
              {displayName}
            </RouterLink>
          </Typography>
          {typeLabel && (
            <Chip
              label={typeLabel}
              size="small"
              sx={{ fontSize: "0.72rem", height: 22, maxWidth: 160, fontWeight: 500 }}
            />
          )}
        </Box>
        {inspiredByName && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, fontStyle: "italic" }}>
            {t("gallery.inspiredBy")}{" "}
            {item.inspired_by_user_id ? (
              <RouterLink
                to={`/profile/${item.inspired_by_alias || item.inspired_by_user_id}`}
                onClick={(e) => e.stopPropagation()}
                style={{ color: "inherit", textDecoration: "none" }}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
              >
                {inspiredByName}
              </RouterLink>
            ) : (
              inspiredByName
            )}
          </Typography>
        )}
        {tryUrl && item.allow_try === 1 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
            <Chip
              icon={<PlayArrowIcon sx={{ fontSize: "14px !important" }} />}
              label={t("gallery.tryTierList")}
              size="small"
              component="a"
              href={tryUrl}
              clickable
              onClick={(e) => {
                e.stopPropagation();
                trackTryClick(item.Id);
              }}
              color="primary"
              variant="outlined"
              sx={{ fontSize: "0.7rem", height: 22 }}
            />
            {item.try_count > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                <PeopleIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                <Typography variant="caption" color="text.secondary">
                  {item.try_count}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
      <CardActions sx={{ px: 2, pb: 1.5 }}>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onVote(item.Id, "like");
          }}
          color={userVote === "like" ? "primary" : "default"}
          disabled={!isAuthenticated}
        >
          {userVote === "like" ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
        </IconButton>
        <Typography variant="caption" sx={{ mr: 1 }}>
          {item.like_count || 0}
        </Typography>

        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onVote(item.Id, "dislike");
          }}
          color={userVote === "dislike" ? "error" : "default"}
          disabled={!isAuthenticated}
        >
          {userVote === "dislike" ? <ThumbDownIcon fontSize="small" /> : <ThumbDownOutlinedIcon fontSize="small" />}
        </IconButton>
        <Typography variant="caption" sx={{ mr: 1 }}>
          {item.dislike_count || 0}
        </Typography>

        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 0.5 }}>
          <VisibilityIcon fontSize="small" sx={{ opacity: 0.6 }} />
          <Typography variant="caption">{item.view_count || 0}</Typography>
        </Box>
      </CardActions>
    </Card>
  );
};

export default GalleryCard;
